import { throwErrorMessage } from 'dashboard/store/utils/api';
import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import * as types from '../mutation-types';
import CannedResponseAPI from '../../api/cannedResponse';

function normalizeCannedLabelIds(raw) {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : Object.values(raw);
  return arr.map(id => Number(id)).filter(id => Number.isInteger(id) && id > 0);
}

function isAbortError(error) {
  return error?.name === 'CanceledError' || error?.name === 'AbortError';
}

// Latest request wins, so a slow search for a previous slash term cannot
// overwrite the response for the term the agent is typing now.
let groupedRequestId = 0;

const state = {
  records: [],
  groupedRecords: [],
  groupedQuery: null,
  uiFlags: {
    fetchingList: false,
    fetchingGroupedList: false,
    fetchingItem: false,
    creatingItem: false,
    updatingItem: false,
    deletingItem: false,
    importingTable: false,
  },
};

const getters = {
  getCannedResponses(_state) {
    return _state.records;
  },
  getGroupedCannedResponses(_state) {
    return _state.groupedRecords;
  },
  getGroupedCannedQuery(_state) {
    return _state.groupedQuery;
  },
  getSortedCannedResponses(_state) {
    return sortOrder =>
      [..._state.records].sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.short_code.localeCompare(b.short_code);
        }
        return b.short_code.localeCompare(a.short_code);
      });
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

const actions = {
  getCannedResponse: async function (
    { commit },
    { searchKey, all = false, inboxId = null, signal } = {}
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: true });
    try {
      const response = await CannedResponseAPI.get({
        searchKey,
        all,
        inboxId,
        signal,
      });
      commit(types.default.SET_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
    } catch (error) {
      if (!isAbortError(error)) {
        commit(types.default.SET_CANNED_UI_FLAG, { fetchingList: false });
        throw error;
      }
    }
  },

  // Fetches canned responses grouped by short_code, ordered by priority for
  // the current user (assigned to them > their team > public). Used by the
  // mention picker in the message composer so duplicate short_codes collapse
  // into a single entry with a "N variants" expander.
  getGroupedCannedResponse: async function (
    { commit },
    { searchKey = '', signal } = {}
  ) {
    groupedRequestId += 1;
    const requestId = groupedRequestId;
    const isLatest = () => requestId === groupedRequestId;

    commit(types.default.SET_CANNED_UI_FLAG, { fetchingGroupedList: true });
    try {
      const response = await CannedResponseAPI.getGrouped({
        searchKey,
        signal,
      });
      if (!isLatest()) return;

      commit(types.default.SET_GROUPED_CANNED, {
        records: response.data,
        query: searchKey,
      });
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingGroupedList: false });
    } catch (error) {
      if (!isLatest()) return;
      commit(types.default.SET_CANNED_UI_FLAG, { fetchingGroupedList: false });
      if (isAbortError(error)) return;
      throw error;
    }
  },

  createCannedResponse: async function createCannedResponse(
    { commit },
    cannedObj
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: true });
    try {
      const payload = {
        canned_response: {
          ...cannedObj,
          label_ids: normalizeCannedLabelIds(cannedObj.label_ids),
        },
      };
      const response = await CannedResponseAPI.create(payload);
      commit(types.default.ADD_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { creatingItem: false });
      return throwErrorMessage(error);
    }
  },

  updateCannedResponse: async function updateCannedResponse(
    { commit },
    { id, ...updateObj }
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: true });
    try {
      const payload = {
        canned_response: {
          ...updateObj,
          label_ids: normalizeCannedLabelIds(updateObj.label_ids),
        },
      };
      const response = await CannedResponseAPI.update(id, payload);
      commit(types.default.EDIT_CANNED, response.data);
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { updatingItem: false });
      return throwErrorMessage(error);
    }
  },

  importCannedResponsesTable: async function importCannedResponsesTable(
    { commit, dispatch },
    { file, visibility, user_ids, team_ids } = {}
  ) {
    commit(types.default.SET_CANNED_UI_FLAG, { importingTable: true });
    try {
      const response = await CannedResponseAPI.importTable(file, {
        visibility,
        user_ids,
        team_ids,
      });
      await dispatch('getCannedResponse', { all: true });
      commit(types.default.SET_CANNED_UI_FLAG, { importingTable: false });
      return response.data;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { importingTable: false });
      return throwErrorMessage(error);
    }
  },

  deleteCannedResponse: async function deleteCannedResponse({ commit }, id) {
    commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
    try {
      await CannedResponseAPI.delete(id);
      commit(types.default.DELETE_CANNED, id);
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
      return id;
    } catch (error) {
      commit(types.default.SET_CANNED_UI_FLAG, { deletingItem: true });
      return throwErrorMessage(error);
    }
  },
};

const mutations = {
  [types.default.SET_CANNED_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.default.SET_CANNED]: MutationHelpers.set,
  [types.default.SET_GROUPED_CANNED](_state, data) {
    _state.groupedRecords = data.records;
    _state.groupedQuery = data.query;
  },
  [types.default.ADD_CANNED]: MutationHelpers.create,
  [types.default.EDIT_CANNED]: MutationHelpers.update,
  [types.default.DELETE_CANNED]: MutationHelpers.destroy,
};

export default {
  state,
  getters,
  actions,
  mutations,
};
